import React, { useId } from "react";

/* --------------------------------------------------------------------------
   Illustrations for the complete-sprint dialog (DeleteConfirmationModal).

   Inline SVG, animated from modal.scss (`.pj-art`), so they stay crisp at any
   size, weigh nothing, and pick up prefers-reduced-motion.

   "pending" — open work: a card lifts out of this sprint and lands in the next.
   "done"    — everything finished: a check draws itself beside a waving flag.
   -------------------------------------------------------------------------- */

const PendingArt = ({ uid }) => (
  <svg className="pj-art pj-art--pending" viewBox="0 0 320 150" role="img" aria-label="Open issues moving to the next sprint">
    <defs>
      <linearGradient id={`${uid}-amber`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#ffd27a" />
        <stop offset="1" stopColor="#f0a21e" />
      </linearGradient>
      <linearGradient id={`${uid}-green`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#3ddc91" />
        <stop offset="1" stopColor="#00ac56" />
      </linearGradient>
    </defs>

    <ellipse className="pj-art-shadow" cx="160" cy="136" rx="128" ry="7" />

    {/* this sprint */}
    <g className="pj-art-panel">
      <rect x="26" y="22" width="108" height="106" rx="16" fill="#ffffff" stroke="#e6e9f0" strokeWidth="1.5" />
      <rect x="40" y="34" width="44" height="8" rx="4" fill={`url(#${uid}-amber)`} />
      <circle cx="118" cy="38" r="4" fill="#f3dca8" />
      <rect x="40" y="52" width="80" height="18" rx="6" fill="#f4f6fa" />
      <rect x="48" y="59" width="36" height="4" rx="2" fill="#c7cedb" />
      <rect x="40" y="76" width="80" height="18" rx="6" fill="#f4f6fa" />
      <rect x="48" y="83" width="48" height="4" rx="2" fill="#c7cedb" />
      <rect className="pj-art-slot pj-art-slot--from" x="40" y="100" width="80" height="18" rx="6" />
    </g>

    {/* next sprint */}
    <g className="pj-art-panel pj-art-panel--next">
      <rect x="186" y="22" width="108" height="106" rx="16" fill="#ffffff" stroke="#e6e9f0" strokeWidth="1.5" />
      <rect x="200" y="34" width="44" height="8" rx="4" fill={`url(#${uid}-green)`} />
      <circle cx="278" cy="38" r="4" fill="#bfe6d1" />
      <rect x="200" y="52" width="80" height="18" rx="6" fill="#f4f6fa" />
      <rect x="208" y="59" width="44" height="4" rx="2" fill="#c7cedb" />
      <rect className="pj-art-slot pj-art-slot--to" x="200" y="100" width="80" height="18" rx="6" />
    </g>

    {/* the path it travels */}
    <path className="pj-art-trail" d="M 96 96 C 130 40, 190 40, 236 96" />
    <path className="pj-art-arrowhead" d="M 229 88 L 237 97 L 240 85" />

    {/* the card on the move */}
    <g className="pj-art-card">
      <rect x="40" y="100" width="80" height="18" rx="6" fill={`url(#${uid}-amber)`} />
      <circle cx="50" cy="109" r="3.5" fill="#ffffff" />
      <rect x="58" y="107" width="40" height="4" rx="2" fill="#ffffff" opacity="0.9" />
    </g>

    <g className="pj-art-sparkles" fill="#00ac56">
      <path className="pj-art-sparkle" d="M 300 18 l 2 5 l 5 2 l -5 2 l -2 5 l -2 -5 l -5 -2 l 5 -2 z" />
      <circle className="pj-art-sparkle pj-art-sparkle--2" cx="172" cy="18" r="2.5" fill="#f0a21e" />
      <circle className="pj-art-sparkle pj-art-sparkle--3" cx="16" cy="60" r="2" fill="#0e5fc1" />
    </g>
  </svg>
);

const DoneArt = ({ uid }) => (
  <svg className="pj-art pj-art--done" viewBox="0 0 320 150" role="img" aria-label="Sprint completed">
    <defs>
      <linearGradient id={`${uid}-badge`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor="#3ddc91" />
        <stop offset="0.55" stopColor="#00ac56" />
        <stop offset="1" stopColor="#03854a" />
      </linearGradient>
    </defs>

    <ellipse className="pj-art-shadow" cx="160" cy="136" rx="110" ry="7" />

    {/* expanding rings */}
    <circle className="pj-art-ring" cx="170" cy="74" r="44" />
    <circle className="pj-art-ring pj-art-ring--late" cx="170" cy="74" r="44" />

    {/* check badge */}
    <g className="pj-art-badge">
      <circle cx="170" cy="74" r="44" fill={`url(#${uid}-badge)`} />
      <circle cx="170" cy="74" r="36" fill="none" stroke="#ffffff" strokeOpacity="0.25" strokeWidth="2" />
      <path className="pj-art-check" d="M 150 75 L 164 89 L 191 60" />
    </g>

    {/* finish flag */}
    <g className="pj-art-flag-group">
      <rect x="70" y="30" width="4" height="100" rx="2" fill="#344054" />
      <circle cx="72" cy="28" r="5" fill="#f0a21e" />
      <g className="pj-art-flag">
        <path d="M 74 34 C 94 28, 104 42, 124 36 L 124 70 C 104 76, 94 62, 74 68 Z" fill="#101828" />
        <path d="M 74 34 C 84 31, 89 34, 94 36 L 94 51 C 89 49, 84 47, 74 51 Z" fill="#ffffff" />
        <path d="M 94 51 C 99 53, 104 55, 109 55 L 109 70 C 104 70, 99 68, 94 66 Z" fill="#ffffff" opacity="0.95" />
        <path d="M 109 38 C 114 38, 119 37, 124 36 L 124 53 C 119 54, 114 55, 109 55 Z" fill="#ffffff" opacity="0.95" />
      </g>
    </g>

    {/* confetti */}
    <g className="pj-art-confetti">
      <rect className="pj-art-bit pj-art-bit--1" x="228" y="24" width="7" height="4" rx="1" fill="#f0a21e" />
      <rect className="pj-art-bit pj-art-bit--2" x="250" y="52" width="6" height="6" rx="1.5" fill="#0e5fc1" />
      <circle className="pj-art-bit pj-art-bit--3" cx="238" cy="96" r="3.5" fill="#00ac56" />
      <rect className="pj-art-bit pj-art-bit--4" x="118" y="102" width="7" height="4" rx="1" fill="#7a5af5" />
      <circle className="pj-art-bit pj-art-bit--5" cx="132" cy="18" r="3" fill="#e0463b" />
      <rect className="pj-art-bit pj-art-bit--6" x="270" y="84" width="5" height="5" rx="1" fill="#14a394" />
      <path className="pj-art-bit pj-art-bit--7" d="M 214 12 l 2 5 l 5 2 l -5 2 l -2 5 l -2 -5 l -5 -2 l 5 -2 z" fill="#f0a21e" />
    </g>
  </svg>
);

const SprintCompleteArt = ({ variant = "pending" }) => {
  // useId returns ":r0:"-style ids; colons are awkward inside url(#…)
  const uid = `pjart${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <div className="pj-art-frame">
      {variant === "done" ? <DoneArt uid={uid} /> : <PendingArt uid={uid} />}
    </div>
  );
};

export default SprintCompleteArt;
